import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectModalTypes } from '../../../../../core/legacy/reducers/modals';
import editProjectSections from '../../../../../core/project/commands/EditProjectSections';
import { currentProjectIdQuery, selectedProjectSectionsQuery } from '../../../../../core/project/queries';
import { useAppDispatch, useAppSelector } from '../../../../../types';
import GenericModal from '../../../../legacy/components/ui/modals/GenericModal';
import { ContentContainer, ModalTitle } from '../../../../legacy/components/ui/modals/styles';
import { useProjectSnackbar } from '../../ProjectSnackbarContext';

const EMPTY_VALUE = '';

const EditProjectSectionPriorityModal = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const checkedProjectSections = useAppSelector(selectedProjectSectionsQuery);
    const checkedProjectSectionsIds = checkedProjectSections.map((s) => s.id);
    const currentProjectId = useAppSelector(currentProjectIdQuery);
    const { setSnackbarMessage } = useProjectSnackbar();

    const [priority, setPriority] = useState<string>(EMPTY_VALUE);
    const valueOptions = ['P1', 'P2', 'P3'];

    useEffect(() => {
        const priorities = checkedProjectSections
            .map((ps) => ps.priority)
            .filter((p) => p !== undefined && p !== null)
            .reduce((acc, val) => acc.add(val!), new Set<string>());
        if (priorities.size === 1) {
            setPriority(priorities.values().next().value as string);
        } else {
            setPriority(EMPTY_VALUE);
        }
    }, [checkedProjectSections]);

    const onValidate = async () => {
        if (!currentProjectId) {
            setSnackbarMessage(t('projectList.error.noProjectSelected'));

            return;
        }

        try {
            await dispatch(
                editProjectSections({
                    projectSectionData: {
                        priority: priority === EMPTY_VALUE ? null : (priority as 'P1' | 'P2' | 'P3'),
                    },
                    projectSectionIds: checkedProjectSectionsIds,
                }),
            ).unwrap();
            setPriority(EMPTY_VALUE);
        } catch (err) {
            console.error('Error editing project sections:', err);
            setSnackbarMessage(t('projectList.error.default'));
        }
    };

    const handleChange = (event: SelectChangeEvent<string>) => {
        setPriority(event.target.value);
    };

    return (
        <GenericModal
            onValidate={onValidate}
            modalType={ProjectModalTypes.PROJECT_SECTION_EDIT_PRIORITY}
            options={{ notDismissableByClickingOnBackground: true }}
        >
            <ContentContainer>
                <ModalTitle>{t('projectList.modals.editPriority')}</ModalTitle>
                <FormControl fullWidth variant="standard">
                    <Select value={priority} onChange={handleChange} autoFocus>
                        <MenuItem value={EMPTY_VALUE}>-</MenuItem>
                        {valueOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </ContentContainer>
        </GenericModal>
    );
};

export default EditProjectSectionPriorityModal;
